import { useMemo, useState, type ReactNode } from 'react';
import { Select } from 'antd';
import type { SelectProps } from 'antd';
import { formatStock } from '@app/shared';
import { useSearchCategoriesQuery } from '../api/categoryApi';
import type { CategoryDto } from '../model/types';

/** Уже выбранный товар: его может не быть в результатах поиска (товар в архиве или за пределами
 * первых 30 строк) — тогда поле показало бы технический идентификатор вместо названия. */
export interface CurrentCategory {
  id: string;
  name: string;
  isArchived?: boolean;
}

/** Опция списка; `category` нет у подписи уже выбранного архивного товара. */
interface CategoryOption {
  value: string;
  disabled: boolean;
  label: ReactNode;
  category?: CategoryDto;
}

export interface CategorySearchSelectProps {
  value?: string;
  onChange: (id: string, category: CategoryDto) => void;
  /** Название выбранного товара — подставляется в список, если поиск его не вернул. */
  current?: CurrentCategory;
  /** Фильтр, а не строка документа: выбор можно снять. */
  allowClear?: boolean;
  onClear?: () => void;
  autoFocus?: boolean;
  disabled?: boolean;
  onKeyDown?: SelectProps['onKeyDown'];
  /** Положение в `LinesGrid` (Enter-навигация, §1). antd кладёт `data-*` на корневой элемент, а не
   *  на поле ввода — сетка сама переводит фокус на поле внутри (см. `focusNavCell`). */
  navRow?: number;
  navCol?: number;
  /** Если задан — после названия каждой категории в списке стоит «…», по нему вызывается правка
   *  (выбор категории при этом не происходит). В поле выбранного значения «…» нет. */
  onEdit?: (category: CategoryDto) => void;
}

/** §5: «в списке виден остаток» — категория + К/Р справа. */
export function CategorySearchSelect({ value, onChange, current, allowClear, onClear, autoFocus, disabled, onKeyDown, navRow, navCol, onEdit }: CategorySearchSelectProps) {
  const [q, setQ] = useState('');
  // Список раскрывает и закрывает сам antd; своё состояние нужно, чтобы закрыть его по клику на «…»:
  // окно правки забирает фокус не сразу, и до тех пор список висел бы поверх него.
  const [open, setOpen] = useState(false);
  const { data = [], isFetching } = useSearchCategoriesQuery({ q: q || undefined, limit: 30 });

  const options = useMemo<CategoryOption[]>(() => {
    const found = data.map((c) => ({
      value: c.id,
      disabled: false,
      category: c,
      label: (
        <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <span>{c.name}</span>
          {c.stock ? <span style={{ color: '#8c8c8c' }}>{formatStock(c.stock)}</span> : null}
        </span>
      ),
    }));
    if (!current || current.id !== value || data.some((c) => c.id === current.id)) return found;
    // Архивный товар выбрать заново нельзя — он остаётся в списке только как подпись значения.
    return [{ value: current.id, disabled: !!current.isArchived, label: <span>{categoryLabel(current)}</span> }, ...found];
  }, [data, current, value]);

  return (
    <Select
      value={value}
      showSearch
      allowClear={allowClear}
      onClear={onClear}
      filterOption={false}
      autoFocus={autoFocus}
      disabled={disabled}
      data-nav-row={navRow}
      data-nav-col={navCol}
      loading={isFetching}
      placeholder="Товар"
      style={{ width: '100%', minWidth: 200 }}
      onSearch={setQ}
      onKeyDown={onKeyDown}
      onChange={(id: string) => {
        const found = data.find((c) => c.id === id);
        if (found) onChange(id, found);
      }}
      options={options}
      open={open}
      onOpenChange={setOpen}
      optionRender={
        onEdit
          ? (option) =>
              renderOptionWithEdit(option, (category) => {
                setOpen(false);
                onEdit(category);
              })
          : undefined
      }
    />
  );
}

/** Строка списка с «…»: название, сразу за ним кнопка правки, остаток — справа. `label` (его же
 *  antd показывает в поле выбранного значения) остаётся без кнопки. */
function renderOptionWithEdit(option: { label?: ReactNode; data: CategoryOption }, onEdit: (category: CategoryDto) => void): ReactNode {
  const category = option.data.category;
  if (!category) return option.label;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span>{category.name}</span>
      <span
        role="button"
        aria-label="Редактировать категорию"
        title="Редактировать категорию"
        style={{ cursor: 'pointer', padding: '0 4px', fontWeight: 700, lineHeight: 1, color: '#8c8c8c' }}
        onClick={(e) => {
          // Клик по «…» — не выбор товара: до строки списка (она выбирает) он не доходит.
          e.stopPropagation();
          onEdit(category);
        }}
      >
        …
      </span>
      {category.stock ? <span style={{ marginLeft: 'auto', color: '#8c8c8c' }}>{formatStock(category.stock)}</span> : null}
    </span>
  );
}

function categoryLabel(category: CurrentCategory): string {
  return category.isArchived ? `${category.name} (в архиве)` : category.name;
}
